import { translate, useLocale } from "../../localization";
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Typography,
} from "@mui/material";
import API from "../../services/API";
import { AppButton, AppTextField } from "../ui";
import {
  buildElementOptimizationPayload,
  createDefaultElementOptimizationForm,
  ELEMENT_OPTIMIZATION_METHOD_OPTIONS,
  ELEMENT_OPTIMIZATION_MODE_OPTIONS,
  getElementOptimizationModeHelperText,
  isCustomElementOptimizationMode,
  isValidElementOptimizationForm,
} from "../../utility/elementOptimization";
import usePasteFilesOnHover from "./usePasteFilesOnHover";
import useElementUploadWorkflow from "./useElementUploadWorkflow";
import "./UploadElementDialog.scss";

const POLL_INTERVAL_MS = 5000;
const IMAGE_INPUT_ACCEPT = "image/png,image/jpeg";
const IMAGE_TYPES = new Set(["image/png", "image/jpeg"]);
const TERMINAL_GENERATION_STATUSES = new Set(["SUCCEEDED", "FAILED"]);
const TRANSIENT_POLL_STATUS_CODES = new Set([429, 500, 502, 503, 504]);

const extractData = (response) => response?.data?.data || response?.data;

const isSupportedImageFile = (file) => {
  if (!file) {
    return false;
  }

  if (IMAGE_TYPES.has(file.type)) {
    return true;
  }

  return /\.(png|jpe?g)$/i.test(file.name);
};

const normalizeImageFile = (file) => {
  if (!file) {
    return null;
  }

  if (file?.name) {
    return file;
  }

  const extension = file?.type === "image/png" ? "png" : "jpg";
  return new File([file], `pasted-image.${extension}`, {
    type: file?.type || "image/jpeg",
  });
};

const getSuggestedName = ({ sourceType, prompt, imageFile }) => {
  if (sourceType === "image" && imageFile?.name) {
    return imageFile.name.replace(/\.[^.]+$/, "").trim();
  }

  return String(prompt || "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 80);
};

const getGenerationStatusText = (generation) => {
  if (!generation) {
    return "";
  }

  if (generation.status === "TEXTURING") {
    return translate("Texturing generated model");
  }

  if (generation.status === "SUCCEEDED") {
    return translate("Generated model is ready to upload");
  }

  if (generation.status === "FAILED") {
    return generation.errorMessage || translate("Generation failed");
  }

  return translate("Generating model");
};

const isTransientPollError = (error) =>
  TRANSIENT_POLL_STATUS_CODES.has(Number(error?.response?.status));

const GenerateElementDialog = ({ open, onClose, onUpload }) => {
  const locale = useLocale();
  const { createElementPayload } = useElementUploadWorkflow();
  const [elementName, setElementName] = useState("");
  const [elementDescription, setElementDescription] = useState("");
  const [sourceType, setSourceType] = useState("text");
  const [prompt, setPrompt] = useState("");
  const [texturePrompt, setTexturePrompt] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [optimization, setOptimization] = useState(createDefaultElementOptimizationForm());
  const [generation, setGeneration] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [generationStarting, setGenerationStarting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [modelViewerReady, setModelViewerReady] = useState(false);
  const [sourceImagePreviewUrl, setSourceImagePreviewUrl] = useState("");
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const isGenerationRunning =
    generationStarting ||
    imageUploading ||
    Boolean(generation?.id && !TERMINAL_GENERATION_STATUSES.has(generation.status));
  const canGenerate =
    !isGenerationRunning &&
    !uploading &&
    (sourceType === "text" ? prompt.trim().length > 0 : Boolean(imageFile));
  const canUpload =
    generation?.status === "SUCCEEDED" &&
    !isGenerationRunning &&
    !uploading &&
    isValidElementOptimizationForm(optimization);
  const isGeneratedModelReady = generation?.status === "SUCCEEDED";
  const statusText = getGenerationStatusText(generation);

  useEffect(() => {
    if (!open || modelViewerReady) {
      return undefined;
    }

    let cancelled = false;
    import("@google/model-viewer")
      .then(() => {
        if (!cancelled) {
          setModelViewerReady(true);
        }
      })
      .catch((previewError) => {
        console.error("Model preview failed to load:", previewError);
      });

    return () => {
      cancelled = true;
    };
  }, [modelViewerReady, open]);

  useEffect(() => {
    if (!imageFile) {
      setSourceImagePreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(imageFile);
    setSourceImagePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [imageFile]);

  const resetState = () => {
    setElementName("");
    setElementDescription("");
    setSourceType("text");
    setPrompt("");
    setTexturePrompt("");
    setImageFile(null);
    setOptimization(createDefaultElementOptimizationForm());
    setGeneration(null);
    setImageUploading(false);
    setGenerationStarting(false);
    setUploading(false);
    setError("");
  };

  const handleClose = () => {
    if (imageUploading || uploading) {
      return;
    }

    onClose();
  };

  const setSourceImageFile = (nextFile) => {
    const file = normalizeImageFile(nextFile);
    if (!file) {
      return;
    }

    if (!isSupportedImageFile(file)) {
      alert(translate("Image must be a PNG or JPG file."));
      return;
    }

    setImageFile(file);
    setGeneration(null);
    setError("");

    if (!elementName.trim()) {
      setElementName(getSuggestedName({ sourceType: "image", imageFile: file }));
    }
  };

  const imagePasteHandlers = usePasteFilesOnHover({
    enabled: open && sourceType === "image" && !isGenerationRunning && !uploading,
    onFiles: (files) => {
      setSourceImageFile(files[0]);
    },
  });

  const handleImageInputChange = (e) => {
    const file = e.target.files?.[0];
    setSourceImageFile(file);
    e.target.value = "";
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    setSourceImageFile(file);
    e.dataTransfer.clearData();
  };

  const handleSourceTypeChange = (nextSourceType) => {
    if (sourceType === nextSourceType) {
      return;
    }

    setSourceType(nextSourceType);
    setGeneration(null);
    setError("");
  };

  const pollGeneration = async (generationId) => {
    const response = await API.get(`ai/models/${generationId}`, {
      requiresAuth: true,
    });
    const nextGeneration = extractData(response);
    setGeneration(nextGeneration);
    return nextGeneration;
  };

  useEffect(() => {
    if (!generation?.id || TERMINAL_GENERATION_STATUSES.has(generation.status)) {
      return undefined;
    }

    let cancelled = false;
    let timerId;

    const schedulePoll = () => {
      timerId = window.setTimeout(async () => {
        if (cancelled) {
          return;
        }

        try {
          const nextGeneration = await pollGeneration(generation.id);
          if (
            !cancelled &&
            nextGeneration?.id &&
            !TERMINAL_GENERATION_STATUSES.has(nextGeneration.status)
          ) {
            schedulePoll();
          }
        } catch (pollError) {
          if (cancelled) {
            return;
          }

          if (isTransientPollError(pollError)) {
            console.warn("AI generation polling delayed:", pollError);
            schedulePoll();
            return;
          }

          console.error("AI generation polling failed:", pollError);
          setError(
            pollError?.response?.data?.message ||
              pollError.message ||
              translate("Polling failed.")
          );
        }
      }, POLL_INTERVAL_MS);
    };

    schedulePoll();

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation?.id, generation?.status, locale]);

  const uploadSourceImage = async () => {
    const response = await API.post(
      "ai/inputs",
      {
        fileName: imageFile.name,
        contentType: imageFile.type || "image/jpeg",
      },
      { requiresAuth: true }
    );
    const uploadData = extractData(response);

    await fetch(uploadData.uploadUrl, {
      method: "PUT",
      body: imageFile,
      headers: {
        "Content-Type": uploadData.contentType || imageFile.type || "image/jpeg",
      },
    });

    return uploadData.sourceKey;
  };

  const handleGenerateClick = async () => {
    if (!canGenerate) {
      return;
    }

    setGeneration(null);
    setError("");

    try {
      let sourceImageKey = "";

      if (sourceType === "image") {
        setImageUploading(true);
        sourceImageKey = await uploadSourceImage();
        setImageUploading(false);
      }

      setGenerationStarting(true);
      const response = await API.post(
        "ai/models",
        {
          sourceType,
          prompt: sourceType === "text" ? prompt.trim() : "",
          sourceImageKey,
          texturePrompt: texturePrompt.trim(),
        },
        { requiresAuth: true }
      );
      const nextGeneration = extractData(response);
      setGeneration(nextGeneration);

      if (!elementName.trim()) {
        setElementName(getSuggestedName({ sourceType, prompt, imageFile }));
      }
    } catch (generateError) {
      console.error("AI generation failed:", generateError);
      setError(
        generateError?.response?.data?.message ||
          generateError.message ||
          translate("AI generation failed.")
      );
      setImageUploading(false);
    } finally {
      setGenerationStarting(false);
    }
  };

  const handleUploadClick = async () => {
    if (!canUpload) {
      return;
    }

    setUploading(true);
    setError("");
    try {
      const elementPayload = await createElementPayload({
        file: new File([], "ai-generated-model.glb", { type: "model/gltf-binary" }),
        name: elementName,
        description: elementDescription,
        optimization: buildElementOptimizationPayload(optimization),
      });
      await onUpload({
        generationId: generation.id,
        element: elementPayload,
      });
      resetState();
      onClose();
    } catch (uploadError) {
      console.error("Generated element upload failed:", uploadError);
      setError(uploadError.message || translate("Generated element upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const generationProgress = generation?.progress || 0;
  const generateLabel = imageUploading
    ? translate("Uploading Image")
    : generationStarting
      ? translate("Starting")
      : isGenerationRunning
        ? translate("Generating")
        : translate("Generate");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "upload-element-dialog__paper" }}
    >
      <DialogTitle className="upload-element-dialog__title">{translate("Generate with AI")}</DialogTitle>
      <DialogContent className="upload-element-dialog__content">
        <Grid container spacing={2} className="upload-element-dialog__form">
          <Grid item xs={12}>
            <ButtonGroup size="small" aria-label={translate("generation source")}>
              <AppButton
                kind={sourceType === "text" ? "primary" : "secondary"}
                onClick={() => handleSourceTypeChange("text")}
                disabled={isGenerationRunning || uploading}
              >{translate("Text")}</AppButton>
              <AppButton
                kind={sourceType === "image" ? "primary" : "secondary"}
                onClick={() => handleSourceTypeChange("image")}
                disabled={isGenerationRunning || uploading}
              >{translate("Image")}</AppButton>
            </ButtonGroup>
          </Grid>

          {sourceType === "text" ? (
            <Grid item xs={12}>
              <AppTextField
                label={translate("Prompt")}
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value);
                  setGeneration(null);
                  setError("");
                }}
                multiline
                minRows={3}
                fullWidth
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Box
                onDragOver={handleImageDragOver}
                onDrop={handleImageDrop}
                onClick={() => fileInputRef.current?.click()}
                {...imagePasteHandlers}
                className="upload-element-dialog__dropzone"
              >
                {imageFile ? (
                  <Box className="upload-element-dialog__selected-file">
                    {sourceImagePreviewUrl && (
                      <img
                        className="upload-element-dialog__selected-image"
                        src={sourceImagePreviewUrl}
                        alt=""
                      />
                    )}
                    <Typography className="upload-element-dialog__dropzone-text">
                      {imageFile.name}
                    </Typography>
                  </Box>
                ) : (
                  <Typography className="upload-element-dialog__dropzone-text">{translate("Drop, paste, or click to select a PNG or JPG source image")}</Typography>
                )}
                <input
                  type="file"
                  accept={IMAGE_INPUT_ACCEPT}
                  className="upload-element-dialog__file-input"
                  ref={fileInputRef}
                  onChange={handleImageInputChange}
                />
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <AppTextField
              label={translate("Texture Guidance")}
              value={texturePrompt}
              onChange={(e) => {
                setTexturePrompt(e.target.value);
                setGeneration(null);
                setError("");
              }}
              helperText={translate("Optional. Generated models are textured automatically.")}
              multiline
              minRows={2}
              fullWidth
            />
          </Grid>

          {(generation || error) && (
            <Grid item xs={12}>
              <Box className="upload-element-dialog__ai-status">
                {generation && generation.status !== "FAILED" && generation.status !== "SUCCEEDED" && (
                  <LinearProgress
                    variant="determinate"
                    value={generationProgress}
                    className="upload-element-dialog__ai-progress"
                  />
                )}
                {statusText && (
                  <Typography className="upload-element-dialog__helper-text">
                    {statusText}
                  </Typography>
                )}
                {isGeneratedModelReady && generation?.modelUrl && modelViewerReady && (
                  <Box className="upload-element-dialog__model-preview">
                    <model-viewer
                      src={generation.modelUrl}
                      camera-controls
                      auto-rotate
                      interaction-prompt="none"
                      exposure="1"
                      shadow-intensity="0.35"
                    />
                  </Box>
                )}
                {generation?.thumbnailUrl && !generation?.modelUrl && (
                  <Box className="upload-element-dialog__ai-preview">
                    <img src={generation.thumbnailUrl} alt="" />
                  </Box>
                )}
                {error && (
                  <Typography className="upload-element-dialog__error-text">
                    {error}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {isGeneratedModelReady && (
            <>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  label={translate("Element Name")}
                  value={elementName}
                  onChange={(e) => setElementName(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  label={translate("Element Description")}
                  value={elementDescription}
                  onChange={(e) => setElementDescription(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <AppTextField
                  select
                  label={translate("Optimization")}
                  value={optimization.mode}
                  onChange={(e) =>
                    setOptimization((prev) => ({ ...prev, mode: e.target.value }))
                  }
                  helperText={getElementOptimizationModeHelperText(optimization.mode)}
                  fullWidth
                >
                  {ELEMENT_OPTIMIZATION_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </AppTextField>
              </Grid>
              {isCustomElementOptimizationMode(optimization.mode) && (
                <>
                  <Grid item xs={12} sm={6}>
                    <AppTextField
                      select
                      label={translate("Method")}
                      value={optimization.method}
                      onChange={(e) =>
                        setOptimization((prev) => ({
                          ...prev,
                          method: e.target.value,
                        }))
                      }
                      fullWidth
                    >
                      {ELEMENT_OPTIMIZATION_METHOD_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </AppTextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <AppTextField
                      label={translate("Target Faces")}
                      type="number"
                      value={optimization.targetFaces}
                      onChange={(e) =>
                        setOptimization((prev) => ({
                          ...prev,
                          targetFaces: e.target.value,
                        }))
                      }
                      inputProps={{ min: 1, step: 1000 }}
                      helperText={translate("Positive integer face target.")}
                      fullWidth
                    />
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions className="upload-element-dialog__actions">
        <AppButton
          kind="ghost"
          onClick={handleClose}
          disabled={imageUploading || uploading}
          className="upload-element-dialog__cancel"
        >{translate("Cancel")}</AppButton>
        <AppButton
          kind="secondary"
          onClick={handleGenerateClick}
          loading={imageUploading || generationStarting || isGenerationRunning}
          loadingLabel={generateLabel}
          disabled={!canGenerate}
        >{translate("Generate")}</AppButton>
        <AppButton
          onClick={handleUploadClick}
          loading={uploading}
          loadingLabel={translate("Uploading Element")}
          disabled={!canUpload}
        >{translate("Upload Element")}</AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateElementDialog;
